import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  Clock,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Zap,
  ArrowRight,
  Building2,
  Scale,
  Gavel,
  Calendar,
  MapPin,
  Building
} from 'lucide-react';

const GovernmentIndustryPage: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 to-indigo-800/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-indigo-600 mr-3" />
                <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wider">Government</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Streamline Your Government Operations
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                Manage public facility maintenance, infrastructure repairs, and municipal operations for government agencies and public services with WorkOrder HQ™. 
                From city maintenance to public safety, keep your government operations running smoothly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/signup?industry=5"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all transform hover:scale-105 shadow-lg"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-indigo-600 bg-white dark:bg-gray-800 border-2 border-indigo-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                >
                  View Pricing
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                14-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <div className="relative">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center">
                    <Building2 className="h-8 w-8 text-indigo-600 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Public Facilities</p>
                      <p className="text-gray-600 dark:text-gray-400">City buildings & infrastructure</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Scale className="h-8 w-8 text-indigo-500 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Compliance</p>
                      <p className="text-gray-600 dark:text-gray-400">Regulatory & safety standards</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Gavel className="h-8 w-8 text-indigo-700 mr-4" />
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">Public Services</p>
                      <p className="text-gray-600 dark:text-gray-400">Emergency & safety systems</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Government-Specific Features */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Built for Government Agencies
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage your government operations efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-indigo-100 dark:bg-indigo-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Public Service Scheduling
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Schedule maintenance around public events, meetings, and service hours to minimize citizen disruption.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Infrastructure Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track maintenance of public buildings, roads, utilities, and municipal infrastructure.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-green-100 dark:bg-green-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Scale className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Regulatory Compliance
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Ensure compliance with government regulations, safety standards, and public accessibility requirements.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 dark:bg-purple-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Emergency Response
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Handle emergency repairs and public safety issues with priority dispatch and rapid response.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-red-100 dark:bg-red-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Public Records Management
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Maintain detailed records of all maintenance activities for public transparency and accountability.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 dark:bg-orange-900 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Budget Accountability
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Track maintenance costs, budget allocations, and taxpayer-funded projects for fiscal responsibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Common Government Use Cases
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              See how government agencies use WorkOrder HQ™ to streamline operations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Use Case 1 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Building2 className="h-8 w-8 text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Municipalities</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Manage city-wide maintenance operations including public buildings, parks, and municipal infrastructure.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  City hall & municipal building maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Public park & recreation facility upkeep
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Street lighting & traffic system maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Public safety equipment maintenance
                </li>
              </ul>
            </div>

            {/* Use Case 2 */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-indigo-600 mr-3" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Public Services</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Handle maintenance for public service facilities including libraries, community centers, and emergency services.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Library & community center maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Emergency services facility upkeep
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Public transportation facility maintenance
                </li>
                <li className="flex items-center text-gray-600 dark:text-gray-400">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Public utility system maintenance
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Why Government Agencies Choose WorkOrder HQ™
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Serve Citizens Better
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Maintain public facilities and services to provide better experiences for citizens and visitors.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Ensure Public Safety
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Maintain safety systems and emergency equipment to protect citizens and public property.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Maintain Transparency
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Provide detailed records of maintenance activities for public accountability and transparency.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Optimize Taxpayer Dollars
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Track maintenance costs and optimize spending to ensure efficient use of taxpayer funds.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg p-8 text-white">
              <div className="space-y-6">
                <div className="flex items-center">
                  <Clock className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">14 Days</p>
                    <p className="text-indigo-100">Free Trial</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Users className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Up to 10</p>
                    <p className="text-indigo-100">Technicians</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <FileText className="h-8 w-8 mr-4" />
                  <div>
                    <p className="text-3xl font-bold">Unlimited</p>
                    <p className="text-indigo-100">Work Orders</p>
                  </div>
                </div>
                <Link
                  to="/signup?industry=5"
                  className="block w-full bg-white text-indigo-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mt-6"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Government Operations?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join government agencies who trust WorkOrder HQ™ to streamline their operations and better serve citizens.
          </p>
          <Link
            to="/signup?industry=5"
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-indigo-600 bg-white rounded-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <p className="mt-4 text-sm text-indigo-100">
            No credit card required • Full access to all features
          </p>
        </div>
      </section>
    </div>
  );
};

export default GovernmentIndustryPage;
